import * as stylex from "@stylexjs/stylex";
import {
  colors,
  fonts,
  fontSizes,
  spacing,
} from "@softmax/design-system/tokens.stylex";
import { useContext } from "react";
import type { Components } from "react-markdown";
import { MarkdownContext } from "./markdown-context";

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

const demotedHeadings: Readonly<Record<HeadingLevel, HeadingLevel>> = {
  1: 2,
  2: 3,
  3: 4,
  4: 5,
  5: 6,
  6: 6,
};

export function markdownHeading(
  level: HeadingLevel,
): NonNullable<Components["h1"]> {
  return function MarkdownHeading({ children, id }) {
    const { demoteHeadings, variant } = useContext(MarkdownContext);
    const demotedLevel = demoteHeadings ? demotedHeadings[level] : level;
    // A message sits inside a page that owns H1 to H3, so its headings start at H4.
    const renderedLevel =
      variant === "message" && demotedLevel < 4 ? 4 : demotedLevel;
    const Tag = `h${renderedLevel}` as const;
    // An article styles by the source level: reports write their chapters as H4.
    const article = variant === "article";
    return (
      <Tag
        id={id}
        {...stylex.props(
          styles.heading,
          styles[Tag],
          variant === "message" && styles.messageBlock,
          variant === "message" && styles.messageHeading,
          article && level <= 4 && styles.articleHeading,
          article && level > 4 && styles.articleSubheading,
        )}
      >
        {children}
      </Tag>
    );
  };
}

const styles = stylex.create({
  heading: {
    fontFamily: fonts.mono,
    lineHeight: 1.25,
    fontWeight: 500,
    marginTop: spacing.space32,
    marginBottom: spacing.space12,
    scrollMarginTop: spacing.space24,
  },
  h1: { fontSize: fontSizes.pageTitleWide },
  h2: { fontSize: fontSizes.pageTitle },
  h3: { fontSize: fontSizes.title },
  h4: { fontSize: fontSizes.sectionTitle },
  h5: { fontSize: fontSizes.body },
  h6: { fontSize: fontSizes.body },
  // A message keeps its blocks close and flush with its card's edges.
  messageBlock: {
    marginBlock: 0,
    marginTop: { default: spacing.space8, ":first-child": 0 },
  },
  messageHeading: { fontSize: fontSizes.body, marginBottom: 0 },
  articleHeading: {
    color: colors.foreground,
    fontSize: 20,
    fontWeight: 600,
    letterSpacing: "-0.4px",
    lineHeight: 1.3,
    marginBottom: 14,
    marginTop: { default: 35, ":first-child": 0 },
  },
  articleSubheading: {
    color: colors.foreground,
    fontSize: 17,
    fontWeight: 500,
    letterSpacing: "-0.17px",
    lineHeight: 1.35,
    marginBottom: 10,
    marginTop: { default: 26, ":first-child": 0 },
  },
});
